import { useState, useEffect, useRef } from 'react'
import { ArrowRight, Mail, CheckCircle2 } from 'lucide-react'

function useCounter(target, duration = 1800, start = false) {
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (!start) return
    let raf, t0
    const step = (t) => {
      if (!t0) t0 = t
      const p = Math.min(1, (t - t0) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setVal(Math.round(target * eased))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [start, target, duration])
  return val
}

function ChiffreCle({ n, suffix = '', label, sub, start }) {
  const val = useCounter(n, 1600, start)
  const formatted = val.toLocaleString('fr-FR')
  return (
    <div className="cdg-stat">
      <div className="cdg-stat-num">{formatted}<span className="cdg-stat-suf">{suffix}</span></div>
      <div className="cdg-stat-lab">{label}</div>
      {sub && <div className="cdg-stat-sub">{sub}</div>}
    </div>
  )
}

export function ChiffresCles() {
  const ref = useRef(null)
  const [start, setStart] = useState(false)
  useEffect(() => {
    if (!ref.current) return
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setStart(true), { threshold: 0.3 })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [])

  return (
    <section className="cdg-stats" ref={ref}>
      <div className="cdg-container">
        <div className="cdg-stats-head">
          <span className="cdg-eyebrow">Le CDG31 en chiffres</span>
          <h2>Au service du territoire depuis <em>1987</em></h2>
        </div>
        <div className="cdg-stats-grid">
          <ChiffreCle n={450} label="collectivites affiliees" sub="Communes, EPCI et etablissements publics" start={start} />
          <ChiffreCle n={28000} label="agents accompagnes" sub="Sur l'ensemble de la Haute-Garonne" start={start} />
          <ChiffreCle n={120} suffix="+" label="concours par an" sub="Tous cadres d'emploi confondus" start={start} />
          <ChiffreCle n={98} suffix=" %" label="de satisfaction" sub="Enquete usagers 2025" start={start} />
        </div>
      </div>
    </section>
  )
}

export function CarteHG() {
  const [active, setActive] = useState(null)
  const zones = [
    { id: 'toulouse', nom: 'Toulouse Metropole', n: 37, x: 48, y: 52 },
    { id: 'sicoval', nom: 'Le Sicoval', n: 36, x: 60, y: 58 },
    { id: 'muret', nom: 'Muretain Agglo', n: 16, x: 40, y: 60 },
    { id: 'frontonnais', nom: "Val'Aigo / Frontonnais", n: 42, x: 52, y: 28 },
    { id: 'lauragais', nom: 'Lauragais', n: 58, x: 70, y: 62 },
    { id: 'comminges', nom: 'Comminges / Pyrenees', n: 180, x: 35, y: 88 },
    { id: 'volvestre', nom: 'Volvestre', n: 41, x: 30, y: 70 },
    { id: 'save', nom: 'Save / Coteaux', n: 40, x: 30, y: 45 },
  ]
  const activeZone = zones.find(z => z.id === active)

  return (
    <section className="cdg-carte">
      <div className="cdg-container">
        <div className="cdg-carte-grid">
          <div className="cdg-carte-info">
            <span className="cdg-eyebrow">Notre territoire</span>
            <h2>Une presence sur toute la <em>Haute-Garonne</em></h2>
            <p>Le CDG31 accompagne <strong>450 collectivites</strong> et leurs agents, de la Metropole toulousaine aux vallees pyreneennes. Conseillers de proximite, permanences locales, deplacements a la demande.</p>
            <div className="cdg-carte-stats">
              <div><strong>8</strong><span>bassins d'emploi</span></div>
              <div><strong>12</strong><span>conseillers mobiles</span></div>
              <div><strong>100 %</strong><span>du departement couvert</span></div>
            </div>
            <a className="cdg-btn cdg-btn-primary" href="#">Trouver mon referent<ArrowRight size={16} /></a>
          </div>
          <div className="cdg-carte-visual">
            <svg viewBox="0 0 100 100" className="cdg-carte-svg" preserveAspectRatio="xMidYMid meet">
              <path d="M 45 8 L 58 10 L 62 18 L 70 22 L 75 30 L 72 40 L 78 48 L 76 58 L 82 64 L 78 72 L 70 78 L 68 86 L 58 92 L 48 96 L 38 94 L 28 88 L 22 78 L 24 68 L 18 58 L 22 48 L 20 38 L 26 30 L 30 22 L 38 16 Z"
                fill="var(--cdg-blue-soft)" stroke="var(--cdg-blue)" strokeWidth="0.6" />
              {zones.map(z => (
                <g key={z.id} onMouseEnter={() => setActive(z.id)} onMouseLeave={() => setActive(null)} style={{ cursor: 'pointer' }}>
                  <circle cx={z.x} cy={z.y} r={active === z.id ? 4.2 : 2.8}
                    fill={active === z.id ? 'var(--cdg-red)' : 'var(--cdg-blue)'}
                    style={{ transition: 'all .2s' }} />
                  <circle cx={z.x} cy={z.y} r={active === z.id ? 6 : 0}
                    fill="var(--cdg-red)" opacity="0.2"
                    style={{ transition: 'all .2s' }} />
                </g>
              ))}
            </svg>
            {activeZone && (
              <div className="cdg-carte-tip" style={{ left: activeZone.x + '%', top: activeZone.y + '%' }}>
                <strong>{activeZone.nom}</strong>
                <span>{activeZone.n} collectivites</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export function Partenaires() {
  const logos = [
    { nom: 'Toulouse Metropole', abbr: 'TM' },
    { nom: 'Conseil departemental 31', abbr: 'CD31' },
    { nom: 'Region Occitanie', abbr: 'RO' },
    { nom: 'CNFPT', abbr: 'CNFPT' },
    { nom: 'Prefecture', abbr: 'Pref.' },
    { nom: 'AMF31', abbr: 'AMF31' },
    { nom: 'ANDCDG', abbr: 'ANDCDG' },
    { nom: 'France Services', abbr: 'FS' },
  ]
  return (
    <section className="cdg-partners">
      <div className="cdg-container">
        <div className="cdg-partners-head">
          <span className="cdg-eyebrow cdg-eyebrow--muted">Ils nous font confiance</span>
          <h3>Partenaires institutionnels et collectivites affiliees</h3>
        </div>
        <div className="cdg-partners-row">
          {logos.map(l => (
            <div key={l.nom} className="cdg-partner" title={l.nom}>
              <span className="cdg-partner-abbr">{l.abbr}</span>
              <span className="cdg-partner-nom">{l.nom}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export function Temoignages() {
  const [idx, setIdx] = useState(0)
  const items = [
    {
      texte: "Le CDG31 est un partenaire indispensable pour notre commune. Leur expertise statutaire et leur accompagnement sur le recrutement nous font gagner un temps precieux.",
      auteur: 'Marie-Claude Vidal', role: 'Maire de Saint-Orens-de-Gameville', tag: 'Elue',
    },
    {
      texte: "Les formations proposees sont d'une qualite remarquable. J'ai suivi le parcours « manager de service » et je l'ai utilise des la semaine suivante dans mes equipes.",
      auteur: 'Thomas Lacoste', role: 'Responsable urbanisme, Mairie de Blagnac', tag: 'Agent',
    },
    {
      texte: "L'equipe sante au travail nous a accompagnes sur la refonte de notre document unique. Professionnels, a l'ecoute, parfaitement dans leur role de conseil.",
      auteur: 'Sandra Espelta', role: 'DGS, Communaute de communes du Frontonnais', tag: 'DGS',
    },
  ]
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % items.length), 7000)
    return () => clearInterval(id)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const t = items[idx]
  return (
    <section className="cdg-temoin">
      <div className="cdg-container">
        <div className="cdg-temoin-grid">
          <div className="cdg-temoin-aside">
            <span className="cdg-eyebrow">Temoignages</span>
            <h2>La parole a celles et ceux qui <em>font le service public</em> en Haute-Garonne.</h2>
            <div className="cdg-temoin-dots">
              {items.map((_, i) => (
                <button key={i} className={'cdg-temoin-dot' + (i === idx ? ' active' : '')} onClick={() => setIdx(i)} aria-label={'Temoignage ' + (i + 1)} />
              ))}
            </div>
          </div>
          <figure className="cdg-temoin-card">
            <div className="cdg-temoin-quote">&ldquo;</div>
            <blockquote>{t.texte}</blockquote>
            <figcaption>
              <div className="cdg-temoin-avatar">{t.auteur.split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
              <div>
                <div className="cdg-temoin-nom">{t.auteur}</div>
                <div className="cdg-temoin-role">{t.role}</div>
              </div>
              <span className="cdg-badge cdg-badge-yellow" style={{ marginLeft: 'auto' }}>{t.tag}</span>
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  )
}

export function Newsletter() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const submit = (e) => { e.preventDefault(); if (email.includes('@')) setSent(true) }

  return (
    <section className="cdg-news">
      <div className="cdg-container">
        <div className="cdg-news-inner">
          <div className="cdg-news-ic"><Mail size={28} /></div>
          <div className="cdg-news-text">
            <h3>Recevez la lettre du CDG31</h3>
            <p>Actualites statutaires, nouveaux concours, formations a venir. 1 envoi par mois, desinscription en 1 clic.</p>
          </div>
          {!sent ? (
            <form className="cdg-news-form" onSubmit={submit}>
              <input type="email" required placeholder="votre.email@collectivite.fr"
                value={email} onChange={e => setEmail(e.target.value)} />
              <button className="cdg-btn cdg-btn-primary" type="submit">
                S'abonner<ArrowRight size={16} />
              </button>
            </form>
          ) : (
            <div className="cdg-news-ok">
              <CheckCircle2 size={22} /> Merci&nbsp;! Un email de confirmation vous a ete envoye.
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
